import type { BatteryOutlook } from "@spf/contracts";
import { Card } from "../Card.js";
import { cn } from "../../lib/cn.js";

/**
 * The design's twenty-four equaliser bars, kept exactly as it draws them.
 *
 * They are decoration and carry no measurement -- the pattern is fixed, not
 * sampled from anything -- so they are `aria-hidden` and the figure above says
 * everything the widget knows.
 */
const BARS = [
  { height: 35, lit: true },
  { height: 50, lit: true },
  { height: 25, lit: false },
  { height: 70, lit: true },
  { height: 40, lit: false },
  { height: 85, lit: true },
  { height: 30, lit: false },
  { height: 60, lit: true },
  { height: 45, lit: true },
  { height: 20, lit: false },
  { height: 75, lit: true },
  { height: 90, lit: true },
  { height: 55, lit: false },
  { height: 35, lit: false },
  { height: 65, lit: true },
  { height: 80, lit: true },
  { height: 40, lit: false },
  { height: 30, lit: false },
  { height: 85, lit: true },
  { height: 60, lit: true },
  { height: 45, lit: false },
  { height: 25, lit: false },
  { height: 70, lit: true },
  { height: 40, lit: false },
] as const;

export function EstimatedFullWidget({
  outlook,
  onExpand,
  className,
}: {
  outlook: BatteryOutlook | null;
  onExpand?: () => void;
  className?: string;
}) {
  const minutes =
    outlook === null || outlook.timeToFullHours === null
      ? null
      : Math.round(outlook.timeToFullHours * 60);

  return (
    <Card
      onExpand={onExpand}
      title="Estimated Full"
      className={cn("flex flex-col justify-between", className)}
    >
      <div>
        {minutes === null ? (
          // Null rather than zero, and a sentence rather than a dash: the
          // battery is not charging, so there is no time to full to state.
          <p className="text-sm text-ink-muted">
            Not charging, so there is nothing to count down to.
          </p>
        ) : (
          <p className="tabular flex items-baseline gap-1">
            <span className="text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              {Math.floor(minutes / 60)}
            </span>
            <span className="mr-2 text-base font-normal text-ink-muted sm:text-lg">hr</span>
            <span className="text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              {minutes % 60}
            </span>
            <span className="text-base font-normal text-ink-muted sm:text-lg">mm</span>
          </p>
        )}
      </div>

      <div aria-hidden="true" className="mt-8 pt-2">
        <div className="flex h-14 w-full items-end justify-between gap-[3px] sm:gap-1">
          {BARS.map((bar, index) => (
            <div
              key={index}
              className={cn(
                "w-1 rounded-full transition-all duration-300 sm:w-1.5",
                bar.lit ? "bg-accent" : "bg-ink-muted/45",
              )}
              style={{ height: `${bar.height}%` }}
            />
          ))}
        </div>
      </div>
    </Card>
  );
}
